/**
 * Products Promptive has built and operates itself — as opposed to the
 * client services listed under /paslaugos.
 *
 * Single source of truth for the homepage ProductsSection and the /produktai
 * page, so adding a second product means editing this file only.
 *
 * Locale-specific copy is nested per product; locale-neutral fields (url, name,
 * accent) live at the top level.
 */

export type ProductFeature = {
  icon: string;
  title: string;
  description: string;
};

export type ProductCopy = {
  tagline: string;
  shortDescription: string;
  description: string;
  features: ProductFeature[];
  highlights: string[];
};

export type Product = {
  id: string;
  name: string;
  url: string;
  /**
   * Tailwind gradient used for the product's buttons and accents.
   *
   * These must be complete, literal class strings — never assembled from
   * fragments — and `./src/data/**` must stay in tailwind.config.ts `content`,
   * or Tailwind emits no rule for them and the buttons render backgroundless
   * (white label on a white card).
   */
  accent: string;
  /**
   * Optional wordmark asset. Light-on-transparent, so it is only placed on the
   * dark surfaces in ProductsSection and the /produktai header. Products
   * without one fall back to a text wordmark.
   */
  logo?: string;
  /** Intrinsic dimensions of `logo`, required by next/image. */
  logoSize?: { width: number; height: number };
  content: Record<'en' | 'lt', ProductCopy>;
};

export const products: Product[] = [
  {
    id: 'kalba-ai',
    name: 'kalba.ai',
    url: 'https://kalba.ai',
    accent: 'from-violet-600 to-indigo-600',
    logo: '/images/kalba-logo.svg',
    logoSize: { width: 172, height: 48 },
    content: {
      en: {
        tagline: 'AI voice agents that speak real Lithuanian',
        shortDescription:
          'Our own AI voice agent platform. It calls, qualifies and books — in natural Lithuanian, around the clock.',
        description:
          'kalba.ai is a voice AI platform we built and run ourselves. It handles outbound sales calls, answers customer questions 24/7 and books meetings straight into your calendar — speaking Lithuanian that actually sounds Lithuanian, with the same energy on the five-hundredth call as on the first.',
        features: [
          {
            icon: '📞',
            title: 'Outbound calling',
            description: 'Works through your lead lists, qualifies prospects and hands over the ones worth your time.',
          },
          {
            icon: '🎧',
            title: '24/7 customer service',
            description: 'Answers routine customer questions day and night, so nobody waits until morning.',
          },
          {
            icon: '📅',
            title: 'Appointment booking',
            description: 'Books meetings during the call and syncs them straight to your calendar.',
          },
          {
            icon: '📊',
            title: 'Call analytics',
            description: 'Every conversation transcribed and analysed, so you can see what actually converts.',
          },
        ],
        highlights: [
          'Native-quality Lithuanian speech',
          'Never tires, never has an off day',
          'Scales from one call to thousands',
        ],
      },
      lt: {
        tagline: 'AI balso agentai, kalbantys tikra lietuvių kalba',
        shortDescription:
          'Mūsų pačių sukurta AI balso agentų platforma. Skambina, kvalifikuoja ir registruoja susitikimus — natūralia lietuvių kalba, visą parą.',
        description:
          'kalba.ai — balso AI platforma, kurią sukūrėme ir valdome patys. Ji atlieka išeinančius pardavimų skambučius, atsako į klientų klausimus 24/7 ir registruoja susitikimus tiesiai į jūsų kalendorių — kalbėdama lietuviškai taip, kad iš tiesų skamba lietuviškai, ir penkiašimtajame skambutyje turi tiek pat energijos, kiek pirmajame.',
        features: [
          {
            icon: '📞',
            title: 'Išeinantys skambučiai',
            description: 'Perskambina jūsų kontaktų sąrašus, kvalifikuoja klientus ir perduoda tik vertus jūsų laiko.',
          },
          {
            icon: '🎧',
            title: 'Klientų aptarnavimas 24/7',
            description: 'Atsako į įprastus klientų klausimus dieną ir naktį — niekam nereikia laukti ryto.',
          },
          {
            icon: '📅',
            title: 'Susitikimų registracija',
            description: 'Suderina susitikimą pokalbio metu ir įrašo jį tiesiai į jūsų kalendorių.',
          },
          {
            icon: '📊',
            title: 'Skambučių analitika',
            description: 'Kiekvienas pokalbis transkribuojamas ir analizuojamas — matote, kas iš tiesų konvertuoja.',
          },
        ],
        highlights: [
          'Natūraliai skambanti lietuvių kalba',
          'Nepavargsta ir neturi prastų dienų',
          'Veikia nuo vieno iki tūkstančių skambučių',
        ],
      },
    },
  },
];

export function getProductCopy(product: Product, locale: string): ProductCopy {
  return product.content[locale === 'lt' ? 'lt' : 'en'];
}
